import { candidateApplicationConfigSchema } from '../steps/types/candidate-application.js';
export const approvedTruthfulnessStatement = "I attest that the information I have supplied to VIGEO is accurate and truthful, and I will provide accurate and truthful information throughout my employment or contracted service. This applies to verbal, written and electronic information, including patient documentation and billing information. I will not fabricate, falsify or alter records to misrepresent information. I will promptly report known or suspected falsification or other compliance concerns to the Compliance Officer through VIGEO's confidential reporting process. I understand that good-faith reporting is protected from retaliation.";
export const stepDefinitionSeeds = [
    {
        id: 'onboarding-information-truthfulness', type: 'acknowledgment',
        portalGroup: 'read-and-sign',
        satisfies: ['information-truthfulness-statement'], controlledForm: true, autoIssue: true,
        heading: 'Information accuracy and truthfulness statement',
        controlledText: approvedTruthfulnessStatement,
        config: { heading: 'Information accuracy and truthfulness statement', controlledText: approvedTruthfulnessStatement,
            signatureLabel: 'I attest to the statement above', requirementKey: 'information-truthfulness-statement', target: 'employee-records' },
        reviewerHint: 'LD7.25.1; wording approved September 12, 2026. Verify signer, actual signature date and retained signed document.',
    },
    {
        /**
         * Form I-9, tracked here and completed in QuickBooks Workforce (decision D22).
         *
         * This definition exists so the obligation is VISIBLE — to HR, who must chase it, and to
         * the candidate, who would otherwise finish everything the portal asked and be left
         * wondering why their file is incomplete. It collects nothing. The step type has nowhere
         * to put the form, an identity document, a document number, or anyone's citizenship or
         * immigration status, and the portal renders a status rather than a control.
         *
         * It is `external-process`, NOT `pdf-form`. `pdf-form` exists and works, and is used by
         * the other government forms in this catalog; using it here would mean collecting an I-9
         * in this system, which is exactly what the decision forbids.
         */
        id: 'onboarding-i9-external',
        type: 'external-process',
        portalGroup: 'before-first-visit',
        satisfies: ['i9-form'],
        config: {
            heading: 'Form I-9',
            externalSystem: 'quickbooks-workforce',
            candidateGuidance: 'Complete Form I-9 through QuickBooks Workforce. HR will send your QuickBooks Workforce invitation separately, along with the acceptable-document instructions. Form I-9 is completed separately from this portal.',
            instructions: 'Send the QuickBooks Workforce invitation, then confirm here once Section 1 and Section 2 are complete and the final form is retrievable in QuickBooks.',
            externalUrl: null,
            requirementKey: 'i9-form'
        },
        heading: 'Form I-9',
        helpText: 'Completed in QuickBooks Workforce. Nothing is uploaded here.',
        internalNotes: 'Decision D22. QuickBooks Workforce is the system of record on the existing Workforce Premium subscription. The employee completes Section 1; the employer examines acceptable documents and completes Section 2. This dashboard records only where the obligation stands, and completion requires an HR_Admin to confirm BOTH sections are done and the final form is retrievable — an invitation, a document, or the person appearing in the Workforce roster satisfies nothing.',
        reviewerHint: 'Confirm in QuickBooks that Sections 1 and 2 are complete and the final I-9 is retrievable before marking this complete.'
    },
    {
        id: 'onboarding-employment-application',
        type: 'candidate-application',
        portalGroup: 'fill-in',
        satisfies: ['employment-application'],
        config: candidateApplicationConfigSchema.parse({
            heading: 'Your employment application',
            instructions: 'Complete your contact information, emergency contacts, work history, and two references. Include education and credentials relevant to the position. License and certification evidence is collected in the corresponding upload steps.',
            target: 'recruitment-onboarding',
        }),
        heading: 'Your employment application',
        helpText: 'Your signed application is reviewed by HR and filed with your recruitment records.',
        internalNotes: 'Structured application replacing the upload-only definition for new issuances. Existing submitted upload instances retain their pinned type and content.',
    },
    {
        id: 'onboarding-tb-baseline-evidence', type: 'document-upload',
        /*
         * "Acknowledge before your first visit" in 3.1, NOT "Upload", even though this is a
         * `document-upload` today — and the divergence is the point of recording it here.
         *
         * The frame draws form 13 as an acknowledgement that the candidate will provide TB clearance;
         * the app currently asks for the document itself. The group follows the DESIGN, because this
         * field exists to make 3.1 render as drawn. When the acknowledge-then-provide shape is built,
         * the step type changes and this line does not.
         */
        portalGroup: 'before-first-visit',
        satisfies: ['tb-baseline-risk-assessment'], contribution: 'contributes', autoIssue: true,
        config: {
            heading: 'TB512 or accepted PCP TB documentation',
            instructions: 'Use the VIGEO TB512 form with your qualified screener, or provide acceptable PCP TB documentation. Upload the completed document here. HR must separately review the actual findings, dates and any required follow-up before this requirement is cleared.',
            documents: [{ label: 'Download VIGEO TB512 (November 2021)', url: '/forms/VIGEO-TB512-November-2021.pdf' }],
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png'], maxSizeMb: 20,
            target: 'segregated-health', requirementKey: null, collectsExpiry: false,
        },
        heading: 'TB512 or accepted PCP TB documentation',
        reviewerHint: 'File in restricted health records. Review the completed screener or accepted PCP evidence and record the reviewed baseline separately; uploading or filing alone does not establish clinical clearance.',
    },
    {
        id: 'onboarding-work-history',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['work-history'],
        config: {
            heading: 'Your work history',
            instructions: 'Send a resume or a written list of your employers and employment dates. If you have no prior employment, state that for HR review. Employment history is reviewed separately from the two completed reference checks.',
            acceptedTypes: [
                'application/pdf',
                'image/jpeg',
                'image/png',
                'image/heic',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            ],
            maxSizeMb: 20,
            target: 'recruitment-onboarding',
            requirementKey: null,
            collectsExpiry: false
        },
        heading: 'Your work history',
        internalNotes: 'Evidence is "Documented work history on file" — a plain document. Checked specifically for a hidden attestation: the neighbouring `hha-24-month-lapse-rule` names a "signed attestation for pre-hire gaps" and this row does not, so no acknowledgment claim is being pinned. Conditional on `reference-chec'
    },
    {
        id: 'onboarding-contract-written-agreement',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['contract-written-agreement'],
        config: {
            heading: 'Your signed agreement with us',
            instructions: 'Send the signed agreement covering the services you provide and your rates. Tell us the date it runs through so we can remind you before it needs renewing.',
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
            maxSizeMb: 20,
            target: 'employee-records',
            requirementKey: null,
            collectsExpiry: true
        },
        heading: 'Your signed agreement with us',
        internalNotes: 'Evidence is "Executed Written Agreement/Contract covering professional services and all charges, copy in the Agency\'s administrative files". Tested against gap:acknowledgment and rejected on the type\'s own shape: #57\'s acknowledgment is typed name plus timestamp with NO file, and cannot carry a coun'
    },
    {
        id: 'onboarding-rn-license',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['rn-license'],
        config: {
            heading: 'Your RN license',
            instructions: 'Send a photo or PDF of your current RN license, and tell us the expiration date printed on it. We need this on file before you can be assigned to a patient.',
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
            maxSizeMb: 20,
            target: 'employee-records',
            requirementKey: 'rn-license',
            collectsExpiry: true
        },
        heading: 'Your RN license',
        internalNotes: 'Evidence noun is "Copy of the current unrestricted state RN license" — a file, nothing signed, no wording attested. Not gap:date-credential: the licence NUMBER is captured on `psv-verification-record-retained` ("verifier name, credential number, status, expiration"), an HR-performed row where HR rea'
    },
    {
        id: 'onboarding-lpn-license',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['lpn-license'],
        config: {
            heading: 'Your LPN/LVN license',
            instructions: 'Send a photo or PDF of your current LPN or LVN license, and tell us the expiration date printed on it. We need this on file before you can be assigned to a patient.',
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
            maxSizeMb: 20,
            target: 'employee-records',
            requirementKey: 'lpn-license',
            collectsExpiry: true
        },
        heading: 'Your LPN/LVN license',
        internalNotes: 'Evidence is "Copy of the current LPN/LVN license" — the file is the evidence, expiry mode credential-date, no signature and no self-reported identifier. The school-verification clause is HR\'s PSV work on a separate row. Gates first-assignment, so it must be seedable now, and it is with no approximat'
    },
    {
        id: 'onboarding-pt-license',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['pt-license'],
        config: {
            heading: 'Your physical therapy license',
            instructions: 'Send a photo or PDF of your current PT license, and tell us the expiration date printed on it. We need this on file before you can be assigned to a patient.',
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
            maxSizeMb: 20,
            target: 'employee-records',
            requirementKey: 'pt-license',
            collectsExpiry: true
        },
        heading: 'Your physical therapy license',
        internalNotes: 'Evidence is "Copy of the current PT license" — the artifact is a file and nothing is attested. Expiry mode credential-date, so the expiration rides along with the upload. The CAPTE/APTA curriculum half is education verification, a separate document row. Gates first-assignment.'
    },
    {
        id: 'onboarding-pta-license',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['pta-license'],
        config: {
            heading: 'Your PTA license or certification',
            instructions: 'Send a photo or PDF of your current PTA license, registration or certification — whichever your state issues — and tell us the expiration date printed on it.',
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
            maxSizeMb: 20,
            target: 'employee-records',
            requirementKey: 'pta-license',
            collectsExpiry: true
        },
        heading: 'Your PTA license or certification',
        internalNotes: 'Evidence is "Copy of the current state PTA license, registration **or** certification (whichever the practice state uses)" — three names for one artifact, so one definition whose instructions name all three. Accepted types and routing target are identical whichever instrument Virginia issues. Expiry'
    },
    {
        id: 'onboarding-ot-license',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['ot-license'],
        config: {
            heading: 'Your occupational therapy license',
            instructions: 'Send a photo or PDF of your current OT license, and tell us the expiration date printed on it. We need this on file before you can be assigned to a patient.',
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
            maxSizeMb: 20,
            target: 'employee-records',
            requirementKey: 'ot-license',
            collectsExpiry: true
        },
        heading: 'Your occupational therapy license',
        internalNotes: 'Evidence is "Copy of the current OT license" — the licence copy closes the row. The catalog\'s "Collapses to education/certification evidence in a state without OT licensure" branch does not change the type (still a file) and does not arise: Virginia is the only operating state under decision 2 and i'
    },
    {
        id: 'onboarding-cota-certification',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['cota-certification'],
        config: {
            heading: 'Your COTA credentials',
            instructions: 'Send your current state OTA license or certification and your NBCOT certification card, plus your program completion certificate if you have it. Tell us the expiration date on your state credential.',
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
            maxSizeMb: 20,
            target: 'employee-records',
            requirementKey: 'cota-certification',
            collectsExpiry: true
        },
        heading: 'Your COTA credentials',
        internalNotes: 'RESOLVED AGAINST gap:date-credential. Evidence is "ACOTE-accredited program graduation, current NBCOT certification in good standing, current state OTA certification/license where regulated" — every element is a document the COTA supplies, with no signature and no identifier the person self-reports,'
    },
    {
        id: 'onboarding-slp-license',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['slp-license'],
        config: {
            heading: 'Your speech-language pathology license',
            instructions: 'Send a photo or PDF of your current SLP license, and tell us the expiration date printed on it. We need this on file before you can be assigned to a patient.',
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
            maxSizeMb: 20,
            target: 'employee-records',
            requirementKey: 'slp-license',
            collectsExpiry: true
        },
        heading: 'Your speech-language pathology license',
        internalNotes: 'Evidence is "Current SLP license plus **either** a master\'s/doctoral degree **or** 350 supervised clinical practicum hours...". Checked the second branch specifically for a hidden attestation: practicum hours and supervised experience are evidenced by transcript and exam result — documents on the ed'
    },
    {
        id: 'onboarding-msw-degree',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['msw-degree'],
        config: {
            heading: 'Your social work degree',
            instructions: 'Send your degree certificate from an accredited social work school, along with something showing at least one year of social work in a health care setting — a resume or a letter from a previous employer is fine.',
            acceptedTypes: [
                'application/pdf',
                'image/jpeg',
                'image/png',
                'image/heic',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            ],
            maxSizeMb: 20,
            target: 'employee-records',
            requirementKey: null,
            collectsExpiry: false
        },
        heading: 'Your social work degree',
        internalNotes: 'Evidence is "Degree from a CSWE-accredited school plus documented 1 year of social work experience in a health care setting" — files, no signature on wording, no self-reported dates, and the catalog states the degree does not expire (expiry mode \'none\'). CONFIG CAVEAT, not a type caveat: the evidenc'
    },
    {
        id: 'onboarding-msw-license',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['msw-license'],
        config: {
            heading: 'Your social work license',
            instructions: 'Send a photo or PDF of your current state social work license, and tell us the expiration date printed on it.',
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
            maxSizeMb: 20,
            target: 'employee-records',
            requirementKey: 'msw-license',
            collectsExpiry: true
        },
        heading: 'Your social work license',
        internalNotes: 'Evidence is "Copy of the current state social work license, **or** a written HR determination that state law does not require licensure — Q49". Only the first branch is person-facing and it is a plain file with one clock (expiry mode credential-date). The second branch is authored by HR — the person'
    },
    {
        id: 'onboarding-hha-qualification-pathway',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['hha-qualification-pathway'],
        config: {
            heading: 'Your home health aide training certificate',
            instructions: 'Send the certificate from your aide training or competency program, your nurse aide program completion, or your state license — whichever one you have. We need it before your first visit.',
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
            maxSizeMb: 20,
            target: 'employee-records',
            requirementKey: null,
            collectsExpiry: false
        },
        heading: 'Your home health aide training certificate',
        internalNotes: 'All four pathways close with a document the aide already holds: "(a) training and competency program of **at least 75 hours** ... (b) certificate from a qualifying competency evaluation program ... (c) state-approved nurse aide program plus current good standing on the state registry; (d) a qualifyi'
    },
    {
        id: 'onboarding-administrator-qualification',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['administrator-qualification'],
        config: {
            heading: 'Proof you qualify as Administrator',
            instructions: 'Send your medical or nursing license, or your degree certificate together with something showing at least one year of supervising or running health services.',
            acceptedTypes: [
                'application/pdf',
                'image/jpeg',
                'image/png',
                'image/heic',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            ],
            maxSizeMb: 20,
            target: 'employee-records',
            requirementKey: null,
            collectsExpiry: false
        },
        heading: 'Proof you qualify as Administrator',
        internalNotes: 'Evidence is "Licensed physician, **or** registered nurse, **or** an undergraduate degree plus health service administration experience including **at least 1 year** supervisory/administrative experience" — the qualifying artifact the person already holds, not a verification act somebody else perform'
    },
    {
        id: 'onboarding-specialty-certification',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['specialty-certification'],
        config: {
            heading: 'Your specialty certification',
            instructions: 'Send a photo or PDF of the certification for the specialty area you have been assigned to, and tell us the expiration date printed on it.',
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
            maxSizeMb: 20,
            target: 'employee-records',
            requirementKey: 'specialty-certification',
            collectsExpiry: true
        },
        heading: 'Your specialty certification',
        internalNotes: 'Evidence is "Copy of the specialty certification for the assigned area of practice" — the plainest document-upload shape, expiry mode credential-date. Safe to seed despite the dead trigger: the rule is conditional on a specialty-practice-assignment record with showBeforeTrigger false, and Q61 record'
    },
    {
        id: 'onboarding-license-copy-at-hire',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['license-copy-at-hire'],
        config: {
            heading: 'A copy of your license or certificate',
            instructions: 'Send a clear, readable copy of the license, certificate or diploma for your role. We need it on file before your first patient visit.',
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
            maxSizeMb: 20,
            target: 'recruitment-onboarding',
            requirementKey: null,
            collectsExpiry: false
        },
        heading: 'A copy of your license or certificate',
        internalNotes: 'Evidence is "Legible copy of the credential, dated and filed before first patient contact" — the artifact is the evidence, with no controlled wording, no third-party judgement and nothing self-reported. collectsExpiry MUST stay false: this row\'s seed expiry is mode \'none\' (one-time collection at hir'
    },
    {
        id: 'onboarding-cpr-certification',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['cpr-certification'],
        config: {
            heading: 'Your CPR/BLS card',
            instructions: 'Send a photo or PDF of your current CPR or BLS card — both sides if the dates are on the back — and tell us the expiration date shown on it.',
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
            maxSizeMb: 20,
            target: 'training-competency',
            requirementKey: 'cpr-certification',
            collectsExpiry: true
        },
        heading: 'Your CPR/BLS card',
        internalNotes: 'Evidence is "Copy of the CPR/BLS card showing issue and expiration". Specifically NOT gap:date-credential: the card image is the evidence and its dates are printed on its face rather than self-reported, so the expiration is reported alongside the file, which is exactly what `collectsExpiry` plus `su'
    },
    {
        id: 'onboarding-cpr-online-inperson-verification',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['cpr-online-inperson-verification'],
        config: {
            heading: 'Your CPR hands-on skills check',
            instructions: 'If you earned your CPR card online, send the dated skills-check sheet signed by the person who watched you do the hands-on part. An online card on its own is not enough.',
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
            maxSizeMb: 20,
            target: 'training-competency',
            requirementKey: null,
            collectsExpiry: true
        },
        heading: 'Your CPR hands-on skills check',
        internalNotes: 'CONTRADICTION RESOLVED (one pass said excluded:hr-recorded, one said document-upload). The exclusion rested on `competency-evaluator-qualification`, and that citation does not reach this row: that requirement governs the discipline-specific clinical competency instruments (HR 4.15A–4.15I) and its pa'
    },
    {
        id: 'onboarding-drivers-license',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['drivers-license'],
        config: {
            heading: 'Your driver\'s license',
            instructions: 'Send a photo of your current driver\'s license and tell us the expiration date on it. Send us a new photo each time you renew.',
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
            maxSizeMb: 20,
            target: 'employee-records',
            requirementKey: 'drivers-license',
            collectsExpiry: true
        },
        heading: 'Your driver\'s license',
        internalNotes: 'Evidence is "Copy of the current license submitted at hire and again at each renewal" — a file, with a renewal cadence the expiring-credential machinery already handles: expiry mode credential-date plus tiers 60/30/14/7/1 is exactly the "new copy at each renewal" the catalog asks for. Attached by th'
    },
    {
        id: 'onboarding-personal-vehicle-insurance',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['personal-vehicle-insurance'],
        config: {
            heading: 'Your car insurance',
            instructions: 'Send your insurance declarations page or insurance card. Make sure the coverage amounts and the dates the policy runs are readable, and tell us the date it ends.',
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
            maxSizeMb: 20,
            target: 'employee-records',
            requirementKey: null,
            collectsExpiry: true
        },
        heading: 'Your car insurance',
        internalNotes: 'Evidence is "Declarations page or insurance card ... with the policy period visible". Checked against the date-credential test and it correctly fails it: the coverage limits are read off the document by HR at review rather than typed in by the driver, and the catalog requires the policy period visib'
    },
    {
        id: 'onboarding-hha-trainer-rn-experience-qualification',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['hha-trainer-rn-experience-qualification'],
        config: {
            heading: 'Your nursing work history',
            instructions: 'Send a resume or written work history showing at least two years of nursing, including at least one year in home health.',
            acceptedTypes: [
                'application/pdf',
                'image/jpeg',
                'image/png',
                'image/heic',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            ],
            maxSizeMb: 20,
            target: 'employee-records',
            requirementKey: 'hha-trainer-rn-experience-qualification',
            collectsExpiry: false
        },
        heading: 'Your nursing work history',
        internalNotes: 'CONTRADICTION RESOLVED (one pass said gap:acknowledgment). Evidence is "Documented work history, resume verification or attestation establishing the experience, for each RN designated as an aide trainer" — three permitted forms, and the FIRST is a plain document, the same artifact everyone agrees is'
    },
    {
        id: 'onboarding-tb-test-baseline',
        type: 'document-upload',
        portalGroup: 'upload',
        satisfies: ['tb-test-baseline'],
        config: {
            heading: 'Your TB test result',
            instructions: 'Send your TB blood test result, or your skin test showing the date it was placed, the date it was read and the reading in millimetres. This is kept in your confidential health file, separate from the rest of your record.',
            acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
            maxSizeMb: 20,
            target: 'segregated-health',
            requirementKey: 'tb-test-baseline',
            collectsExpiry: false
        },
        heading: 'Your TB test result',
        internalNotes: 'Evidence is "IGRA result, or skin test with date placed, date read, induration in mm and reader signature" — a clinic\'s own record that the hire brings with them. The "reader signature" is the clinician\'s on their own record; the person is not signing controlled wording, so this is a file and not an'
    },
    {
        id: 'onboarding-hepb-info-sheet-issued',
        type: 'info-task',
        /*
         * "Health choices" in 3.1, which is the group the frame puts Hepatitis B in — not a group the
         * step type implies. This definition is the INFO SHEET half of form 5; the choice itself
         * (`hepb-offer-and-response`) has no step type yet, so today the candidate reads the sheet and
         * answers elsewhere. Both halves belong to the same group whenever the second one is built.
         */
        portalGroup: 'health-choices',
        satisfies: ['hepb-info-sheet-issued'],
        config: {
            heading: 'Hepatitis B vaccine information',
            bodyText: 'Hepatitis B vaccine information',
            requiresConfirmation: true
        },
        heading: 'Hepatitis B vaccine information',
        internalNotes: 'The ONLY info-task in the whole onboarding slice, and it fits exactly. Evidence is "Record that IC 5.12A.7 was given, as part of the bloodborne pathogens training record" — issuance and receipt. I checked hard for a hidden signature because this row sits beside a declination, and the catalog is prec'
    },
];
export const definitionGaps = [
    {
        requirementKey: 'job-description-signed',
        awaitingType: 'acknowledgment',
        why: '"Discipline-specific JD signed and dated by the employee at hire/orientation, and a new signed copy after each revision" — a signature on the exact wording of a JD carrying duties, qualifications, reporting relationships and ADA physical requirements. Q48\'s re'
    },
    {
        requirementKey: 'patient-visit-rules-acknowledgment',
        awaitingType: 'acknowledgment',
        why: 'The agency\u2019s own Patient Visit Rules page from the pre-onboarding packet (Section 13): a signature on fixed agency wording about conduct in patients\u2019 homes, identical for everyone, which is exactly the "this person read and agreed to these words" shape the acknowledgment type exists for. Paul numbers it form 12 / step 3.23 (#372). Until that step type exists HR records the signed packet page directly, so the row is satisfiable \u2014 it simply has no portal form yet.'
    },
    {
        requirementKey: 'confidentiality-agreement',
        awaitingType: 'acknowledgment',
        why: '"Confidentiality agreement signed and dated" — a signature on uniform agency wording, identical terms for everyone, which is exactly #57\'s "this person read and agreed to these words". Verified `waivable: true` in the seed. Gates first-assignment and belongs t'
    },
    {
        requirementKey: 'conflict-of-interest',
        awaitingType: 'acknowledgment',
        why: '"Signed conflict of interest disclosure" — a signature against the agency\'s standard statement. DESIGN INPUT for #57: the artifact holds disclosure content (the conflicts, if any) alongside the attestation, so the acknowledgment type needs a free-text disclosu'
    },
    {
        requirementKey: 'employee-handbook-acknowledgment',
        awaitingType: 'acknowledgment',
        why: '"Signed acknowledgment of review of the personnel policies or handbook" — the canonical case, and the one issue #52 itself names when explaining why versioning matters. The handbook revises over time and Q56 (re-acknowledgment after a revision) is open, which '
    },
    {
        requirementKey: 'harassment-policy-acknowledgment',
        awaitingType: 'acknowledgment',
        why: '"Signed acknowledgment of HR 4.3.1, typically captured inside the handbook acknowledgment". The nesting note argues FOR the acknowledgment type rather than against it: two definitions can share or nest controlled text and each carry its own hash, whereas two u'
    },
    {
        requirementKey: 'hepb-offer-and-response',
        awaitingType: 'acknowledgment',
        why: 'The most expensive misclassification available in the health set. One of the three accepted closures is "(b) signed and dated IC 5.12B.8 waiver countersigned by the supervisor" on OSHA-prescribed declination wording, and CHAP\'s evidence test is that the record'
    },
    {
        requirementKey: 'visit-signature-policy-attestation',
        awaitingType: 'acknowledgment',
        why: 'The purest signature-on-controlled-wording row in the set: "Signed and dated VIGEO Clinician Policy Attestation Form (clinician name, role, policy reviewed, effective date 2026-08-03, four confirmation checkboxes, signature, printed name, date)". Q45 requires '
    },
    {
        requirementKey: 'evv-policy-acknowledgment',
        awaitingType: 'acknowledgment',
        why: '"Signed acknowledgment of HR 4.26.1 and, where the state Medicaid EVV system applies, training on the six required data elements". The six elements are enumerated INSIDE the attested text, so the hashed controlled text is exactly what must be pinned; an upload'
    },
    {
        requirementKey: 'bloodborne-pathogens-training',
        awaitingType: 'acknowledgment',
        why: '"Signed and dated record covering transmission, the exposure control plan, engineering and work-practice controls, PPE, hazards, and Standard Precautions" — the signature is against an enumerated OSHA curriculum, so the coverage claim lives in the wording, not'
    },
    {
        requirementKey: 'tb-education-training',
        awaitingType: 'acknowledgment',
        why: '"Signed record covering transmission and pathogenesis, latent versus active TB, signs and symptoms, occupational exposure potential". The signed wording includes affirmative duties the person takes on — to seek evaluation on conversion and to report an active '
    },
    {
        requirementKey: 'hazmat-right-to-know-training',
        awaitingType: 'acknowledgment',
        why: '"Signed and dated attendance record" is the entire evidence — document-upload would have nothing to route, and info-task records no signature and cannot carry the 12-month clock. NOTE FOR #57: this row needs an attendance-style acknowledgment whose controlled '
    },
    {
        requirementKey: 'emergency-operations-plan-training',
        awaitingType: 'acknowledgment',
        why: '"Signed and dated attendance record naming the employee\'s role in the plan". The record must name the employee\'s role, so the attested wording is role-specific and must be pinned per definition version — a content-tier hash, not a file. 12-month interval.'
    },
    {
        requirementKey: 'infection-control-training',
        awaitingType: 'acknowledgment',
        why: '"Signed record following the IC 5.4A.5 curriculum — standard precautions, hand hygiene, PPE, disinfection, sharps". The evidentiary question is which curriculum version the signature was given against, which is precisely what content-tier hashing in definition'
    },
    {
        requirementKey: 'workplace-violence-prevention-training',
        awaitingType: 'acknowledgment',
        why: '"Signed record covering what constitutes workplace violence, roles of leadership/staff/law enforcement/security, de-escalation" — a signature against an enumerated content list. The interval is also unresolved (Q18), so the row shows with no due date today and'
    },
    {
        requirementKey: 'safety-roles-responsibilities-training',
        awaitingType: 'acknowledgment',
        why: '"Signed record covering hazardous materials and wastes, infectious materials and wastes, emergency management" — the person signs against a fixed list of safety responsibilities being accepted, and agreement to those specific responsibilities is what must surv'
    },
    {
        requirementKey: 'staff-personal-safety-education',
        awaitingType: 'acknowledgment',
        why: '"Signed record covering home visit safety, general safety practices, self-defense and personal safety, obtaining an escort" — an eleven-item personal-safety curriculum with no artifact for the person to upload and no way for info-task to keep the signature or '
    },
    {
        requirementKey: 'withholding-statements',
        awaitingType: 'pdf-form',
        why: '"Signed and dated federal (and state, if applicable) withholding statements" — the federal W-4 and Virginia VA-4: edition-specific government tax forms signed under penalty of perjury, whose field integrity is the point, replaced on each election change. Treat'
    },
    {
        requirementKey: 'mandated-reporter-acknowledgment',
        awaitingType: 'pdf-form',
        why: 'The official DARS APS "Acknowledgement of Mandated Reporter Status" (Rev 6/2026, Virginia Code 63.2-1606): a state form whose own wording and layout are the evidence, signed on page 1 with the answers overlaid onto the unmodified original (#162, 3.11). The controlled form published from the legacy packet predates this requirement, carries no requirement key and uses older wording, so no seeded definition closes the row yet. Re-signing on a DARS revision follows from the template version pinned in the definition.'
    },
    {
        requirementKey: 'tb-symptom-evaluation',
        awaitingType: 'pdf-form',
        why: '"Documented symptom evaluation (cough ≥3 weeks, unexplained weight loss, night sweats, fever), signed and dated" — four specific questions that must be ANSWERED and the result signed: a controlled instrument with enumerated items. CHAP treats it as its own ele'
    },
];
export const definitionExclusions = [
    { requirementKey: 'compliance-coi-orientation', why: 'excluded:hr-recorded. HR verifies the approved signed or reviewed source evidence and records its version, reviewer and completion through Direct Record (ADR-008).' },
    { requirementKey: 'criminal-sworn-disclosure', why: 'excluded:hr-recorded. HR verifies the approved signed or reviewed source evidence and records its version, reviewer and completion through Direct Record (ADR-008).' },
    { requirementKey: 'respirator-medical-clearance', why: 'excluded:hr-recorded. HR verifies the approved signed or reviewed source evidence and records its version, reviewer and completion through Direct Record (ADR-008).' },
    { requirementKey: 'respirator-fit-test', why: 'excluded:hr-recorded. HR verifies the approved signed or reviewed source evidence and records its version, reviewer and completion through Direct Record (ADR-008).' },
    { requirementKey: 'respirator-training', why: 'excluded:hr-recorded. HR verifies the approved signed or reviewed source evidence and records its version, reviewer and completion through Direct Record (ADR-008).' },
    {
        requirementKey: 'reference-checks',
        why: 'excluded:hr-recorded. "At least two completed reference-check forms, each showing position held, dates of employment, and rehire eligibility" — HR calls the reference and completes the form, and the reference discloses to the Agency, not to the candidate. A ca'
    },
    {
        requirementKey: 'credential-verification',
        why: 'excluded:hr-recorded. "Primary-source confirmation from the school or program; diploma/transcript copy where the degree is the qualifying evidence" — the lead evidence is an outside lookup HR performs. The sibling PSV rows make the principle explicit ("Collect'
    },
    {
        requirementKey: 'conviction-review-determination',
        why: 'excluded:hr-recorded. "Written HR determination recording the decision, special considerations, restrictions, and any additional supervision imposed" — the agency\'s own employment judgement about the person. Conditional and hidden until a conviction disclosure'
    },
    {
        requirementKey: 'wage-salary-record',
        why: 'excluded:hr-recorded. "Current wage or contracted rate plus history of changes" — the Agency sets and records compensation and updates it on each change. A payroll artifact HR maintains; the employee has nothing to submit.'
    },
    {
        requirementKey: 'benefits-record',
        why: 'excluded:hr-recorded. "Benefits enrollment and eligibility documentation" — the agency\'s own enrollment records, maintained by HR/payroll and updated on each election change, structurally identical to `wage-salary-record`. Asking the person to upload it would '
    },
    {
        requirementKey: 'benefits-discussion-at-hire',
        why: 'excluded:hr-recorded. "Record of the benefits discussion. **No form exists — HR 4.22.1 names none** (UNCONFIRMED — Q25)" — HR holds the conversation and records that it happened. There is no artifact to collect (a document-upload could not even write its instr'
    },
    {
        requirementKey: 'emergency-contact',
        why: 'excluded:other. CONTRADICTION RESOLVED (one pass said document-upload). "Emergency contact form naming the contact and phone number. Requirement is *implied* by HR 4.2.1... (UNCONFIRMED — Q58)", and Q58 asks outright whether an emergency contact form exists — '
    },
    {
        requirementKey: 'dcs-qualification',
        why: 'excluded:other — derived, not collected. "Current state nursing registration. Experience lines (3–5 years health care, 2 years supervisory) are marked *preferred* and are not gates", and the expiry rule settles it outright: "Fixed date from the nursing license'
    },
    {
        requirementKey: 'clinical-manager-qualification',
        why: 'excluded:other — derived. "Current state nursing or therapy license plus school verification. Do not hard-code RN — the role is deliberately dual-discipline", with expiry "Fixed date from whichever license the incumbent holds". The artifact is already collecte'
    },
    {
        requirementKey: 'qapi-coordinator-qualification',
        why: 'excluded:other. "Current state nursing license, or evidence of qualified health care professional status". Branch one is rn-license again — close it via `satisfies` (required, per the same seed gate). Branch two has no defined artifact at all: seed expiry is \''
    },
    {
        requirementKey: 'license-attestation-letter',
        why: 'excluded:hr-recorded. "Letter of attestation sent by the institution to the Agency" — the DIRECTION in that sentence is the whole evidentiary value. A copy the candidate hands over is not an institutional attestation; it is a candidate-supplied copy, which is '
    },
    {
        requirementKey: 'psv-verification-record-retained',
        why: 'excluded:hr-recorded. "Written record capturing date, source, method (electronic or telephone), verifier name, credential number, status, expiration and result. **A folder of license photocopies does not satisfy this**" — HR performs the verification against t'
    },
    {
        requirementKey: 'contract-staff-licensed-before-service',
        why: 'excluded:hr-recorded. "**Verified** current license or certification dated **before** the first scheduled patient visit" — the load-bearing word is Verified, and verification is `psv-at-hire` ("Dated verification from the applicable state board"), which the co'
    },
    {
        requirementKey: 'orientation-completion',
        why: 'excluded:hr-recorded. CONTRADICTION RESOLVED (one pass said gap:pdf-form). "Completed HR 4.13A.3 Orientation Checklist — all 41 items marked YES or N/A, signed and dated by the employee and by the supervisor, peer or preceptor who delivered it" — the person wh'
    },
    {
        requirementKey: 'orientation-job-specific-and-equipment-training',
        why: 'excluded:hr-recorded, same instrument and same reasoning as `orientation-completion`. "Orientation Checklist items 12 and 15 signed (job duties, limitations, performance standards, professional boundaries; equipment use per the job description), plus any speci'
    },
    {
        requirementKey: 'new-hire-close-supervision',
        why: 'excluded:hr-recorded. "Documented supervisory oversight during the initial employment period (supervisory visits, joint visits or check-in records), plus evidence that supervision is available at all times in person or by telephone" — the supervisor produces e'
    },
    {
        requirementKey: 'initial-competency-before-independent-care',
        why: 'excluded:hr-recorded. "Completed discipline-specific Initial Competency Checklist, every skill marked, signed by a qualified evaluator (name and title) and by the employee" — a qualified evaluator renders the clinical judgement on the discipline instrument (RN'
    },
    {
        requirementKey: 'hha-initial-onsite-competency',
        why: 'excluded:hr-recorded. "Completed HR 4.15I.14 with the observation setting recorded ... signed by the employee and the observer with title ... **Performed by an RN** in consultation with other skilled professionals" — the performer is named in the catalog text,'
    },
    {
        requirementKey: 'pta-ota-supervising-therapist-assignment',
        why: 'excluded:hr-recorded. "Written record naming the qualified PT (per PTA) or OT (per COTA) responsible for supervision" — an assignment the Agency makes and re-documents on every change (event-driven expiry). A PTA cannot submit who supervises them.'
    },
    {
        requirementKey: 'pre-employment-physical',
        why: 'excluded:other. CONTRADICTION RESOLVED (one pass said document-upload). Flagged UNCONFIRMED — Q14: "**IC 5.15.1 presumes a pre-employment physical exists but no Vigeo policy establishes one, defines its content or names a form.**" Seeding a step would make the'
    },
    {
        requirementKey: 'other-agency-health-requirements',
        why: 'excluded:other. A catch-all placeholder, not a requirement: "Documentation of each additional health requirement the Agency imposes. **The list must be enumerated before this is trackable**" (Q15), and seed expiry is \'unresolved\'. There is no artifact, no word'
    },
];
//# sourceMappingURL=steps.seed.js.map